import React from "react";
import { createPortal } from "react-dom";

import { XCrossIcon } from "../../../assets";
import "../../../assets/scss/pages/helpdesk.scss";
import useTopmostEscape from "./useTopmostEscape";

/**
 * Max width of the dialog. "lg" is the app's standard 780px form modal; "xl"
 * is its widest (600px, then 960px from 992px, then 1160px from 1200px).
 */
export type HelpdeskModalSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASS: Record<HelpdeskModalSize, string> = {
  sm: "helpdeskModal--sm",
  md: "helpdeskModal--md",
  lg: "modal__content--lg780",
  xl: "modal__content--xxxl1160",
};

interface HelpdeskFormModalProps {
  title: string;
  /** Muted text under the title; the header gets a tinted background, as drawn. */
  description?: React.ReactNode;
  onClose: () => void;
  /** Usually Formik's `handleSubmit`. */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  submitDisabled?: boolean;
  cancelLabel?: string;
  /** Hides the Cancel / Save footer (a list pop-up whose actions save on their own); ✕ and Escape still close. */
  hideFooter?: boolean;
  size?: HelpdeskModalSize;
  children: React.ReactNode;
}

/**
 * The app's standard form modal (`modal--hazmatDetail`) for Helpdesk screens:
 * title + ✕, body, Cancel / Save footer. Portalled to <body> so it overlays the
 * whole app, and closes on Escape unless a control inside (an open dropdown)
 * already handled the key. Enter in a field never submits — only the Save
 * button does. When modals are stacked, Escape closes only the top
 * one. Mount only while open so each form starts fresh.
 */
const HelpdeskFormModal: React.FC<HelpdeskFormModalProps> = ({
  title,
  description,
  onClose,
  onSubmit,
  submitLabel = "Save",
  submitDisabled = false,
  cancelLabel = "Cancel",
  size = "lg",
  hideFooter = false,
  children,
}) => {
  useTopmostEscape(onClose);

  // Save runs only from the Save button: Enter in a field (a text input, a
  // dropdown) never submits the form. Enter on a focused button still clicks it,
  // and controls handle their own Enter first (e.g. picking a highlighted option).
  const preventEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (event.key === "Enter" && target.tagName === "INPUT") event.preventDefault();
  };

  return createPortal(
    <div className="modal modal--hazmatDetail modal--open">
      <div className="modal__dialog">
        <form className={`modal__content ${SIZE_CLASS[size]}`} onSubmit={onSubmit} onKeyDown={preventEnterSubmit} noValidate>
          <div className={`modal__header ${description ? "helpdeskModal__header--described" : ""}`.trim()}>
            <h4 className="modal__header--title">{title}</h4>
            {description && <p className="helpdeskModal__description">{description}</p>}
            <button
              type="button"
              className="btn btn__transparent btnClose"
              onClick={onClose}
              aria-label="Close"
            >
              <XCrossIcon />
            </button>
          </div>

          <div className="modal__body modal-body-left">{children}</div>

          {!hideFooter && (
            <div className="modal__footer">
              <div className="btn__group">
                <button type="button" className="btn btn__white" onClick={onClose}>
                  {cancelLabel}
                </button>
                <button type="submit" className="btn btn__primary" disabled={submitDisabled}>
                  {submitLabel}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
      <div className="modal__backdrop" />
    </div>,
    document.body
  );
};

export default HelpdeskFormModal;
